import { createRef, useRef } from "react";
import { Outlet, useMatches, useLocation, Link } from "react-router-dom";

// transition provides component effects (page sliding, menu sliding, etc)
import { CSSTransition, SwitchTransition } from "react-transition-group";

// layout provider context provides header, footer injection as well as display option
import { useLayout } from "../../../providers/LayoutProvider";

// AppShell standard to wrap header, main, and footer
import AppShell from "../../../layout/AppShell";

// content
import { Icon } from "../../../components/icons/Icon";
import { tripTales } from "../components/icons/tripTales";

export default function ToatLayout() {
  const { headerContent, footerContent, showHeader, showFooter } = useLayout();
  const location = useLocation();
  const matches = useMatches();

  /**
   * Initializes pageRefs.current to a new JavaScript Map object during the initial render.
   *   On subsequent re-renders, the same Map object is returned, preserving its contents across renders.
   * This Map will be used to store a collection of individual refs, keyed by their pathnames.
   */
  const pageRefs = useRef(new Map());

  /**
   * This function acts as a memoization layer. It ensures that for a given path,
   * you always get the same reference.
   *
   * If no entry exists, pageRefs.current.set(path, createRef()); is called.
   * It creates a new ref using createRef() and stores it in the Map with the path as the key.
   * If an entry already exists (or after a new one is created),
   * the function returns the existing ref with pageRefs.current.get(path).
   *
   * @param {*} path
   * @returns
   */
  const getStableRefForPath = (path) => {
    if (!pageRefs.current.has(path)) {
      pageRefs.current.set(path, createRef());
    }
    return pageRefs.current.get(path);
  };

  // finds the matching route configuration for the current location.pathname from a list of matches.
  // This pattern is common in routing libraries like React Router.
  const match = matches.find((m) => m.pathname === location.pathname);

  // checks if the matching route already provides a nodeRef via its handle property.
  const routeHandleRef = match?.handle?.nodeRef;

  /**
   * Sets the final nodeRef variable. It uses the ?? (nullish coalescing) operator.
   * It will first try to use the routeHandleRef provided by the router, if it exists.
   * If routeHandleRef is null or undefined, it falls back to calling getStableRefForPath
   *  to either get an existing stable ref from the Map or create a new one for the current path.
   */
  const nodeRef = routeHandleRef ?? getStableRefForPath(location.pathname);

  // transition provides component effects (page sliding, menu sliding, etc)
  // id must equal "layout"
  const main = (
    <main id="layout" role="main" className="layout flex-1">
      <SwitchTransition mode="out-in">
        <CSSTransition
          key={location.pathname}
          nodeRef={nodeRef}
          timeout={300}
          className="page"
          //   unmountOnExit
        >
          {(state) => (
            <div
              key={location.pathname}
              nodeRef={nodeRef}
              // timeout={300}
              className="page"
              unmountOnExit
            >
              <Outlet />
            </div>
          )}
        </CSSTransition>
      </SwitchTransition>
    </main>
  );

  /**
   * App shell layout: header, main content, footer, scroll restore.
   */
  return (
    <AppShell className="toat" dataLayout="toat">
      {showHeader ? headerContent : null}
      {main}
      {showFooter ? footerContent : null}
    </AppShell>
  );
}
